/**
 * Validation rules and copy for the cyber-coverage renewal questions (question #8).
 *
 * The website rules mirror Tlie.Core/Extensions/CyberWebsiteExtensions.cs
 * byte-for-byte. Keep the two in sync — both sides carry the same test table
 * (cyberQuestions.test.ts and Tlie.Tests/CyberWebsiteTests.cs).
 */

// positive integer (1 or greater) — rejects 0, negatives, decimals, letters and spaces.
// Revenue only: the server requires GreaterThan(0) for it, and the field previously used a
// digits-only rule that accepted "0", letting the client pass a value the server then rejected
// with EC336.
export const POSITIVE_INTEGER = /^[1-9]\d*$/

// non-negative integer (0 or greater) — employee count only. TLIE accepts 0 because an entity
// such as a single-member LLC can genuinely have no W-2 employees; the server mirrors this with
// GreaterThanOrEqualTo(0) on EC337. No leading zeros ("01"), negatives, decimals or letters.
export const NON_NEGATIVE_INTEGER = /^(0|[1-9]\d*)$/

// Digit caps matching the backing types, so an over-long entry fails validation rather than blowing
// up JSON deserialization: CyberTotalEmployees is an int, CyberEstimatedAnnualRevenue a decimal.
export const EMPLOYEES_MAX_DIGITS = 9
export const REVENUE_MAX_DIGITS = 15

// $ (without the /m flag) does not match before a trailing newline in JavaScript,
// which is why the C# mirror uses \z rather than $. Input is trimmed first, so
// this is belt-and-braces rather than the sole defence.
const CYBER_WEBSITE = /^www\.([a-z0-9-]+\.)+[a-z]{2,}$/
const LEADING_SCHEME = /^https?:\/\//

// Cyber-quote field messages — shared between the inline inputs and the top-right
// validation panel so the two can never drift. Error codes: EC336/EC337/EC338.
// MSG_WEBSITE_* are duplicated verbatim in RenewalDataValidator.cs so the message
// does not change when a client-side error is replaced by the server's.
export const MSG_REVENUE_REQUIRED = "Firm's estimated annual revenue is required"
export const MSG_REVENUE_FORMAT = 'Please enter a whole number greater than zero, using digits only — no commas, decimals, or letters.'
export const MSG_EMPLOYEES_REQUIRED = 'Total number of employees is required'
export const MSG_EMPLOYEES_FORMAT = 'Please enter a whole number (0 or greater).'
export const MSG_WEBSITE_REQUIRED = 'Firm website is required'
export const MSG_WEBSITE_FORMAT = "Please enter your firm's website starting with www. — for example, www.yourfirm.com."
export const MSG_WEBSITE_LENGTH = 'Firm website must be 99 characters or fewer'

export const CYBER_WEBSITE_MAX_LENGTH = 99

/**
 * Trim, lowercase, drop a leading http:// or https://, and drop one trailing
 * slash. Returns an empty string for null/undefined/whitespace input.
 */
export const normalizeCyberWebsite = (website?: string | null): string => {
  if(!website) return ''

  let normalized = website.trim().toLowerCase()
  if(normalized === '') return ''

  normalized = normalized.replace(LEADING_SCHEME, '')

  if(normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1)
  }

  return normalized
}

/**
 * True when the value, once normalized, starts with "www." and ends in a domain
 * suffix of at least two letters. Paths, ports and query strings are rejected —
 * TLIE wants a bare firm domain.
 */
export const isValidCyberWebsite = (website?: string | null): boolean => {
  const normalized = normalizeCyberWebsite(website)

  return normalized.length > 0 && CYBER_WEBSITE.test(normalized)
}
